import { onMounted, ref, watch } from 'vue';
import type { Swiper } from 'swiper';

export type SwiperContainer = HTMLElement & {
  swiper?: Swiper;
  initialize: () => void;
};

type SwiperParams = Record<string, any>;

// Модули, элемент управления которых лежит внутри самого слайдера
const MODULES_WITH_OWN_ELEMENT = ['scrollbar', 'pagination'];

/**
 * Заменяет селектор `el` у scrollbar и pagination на элемент из этого слайдера.
 * Иначе swiper ищет по всей странице, и все слайдеры цепляются за элемент первого.
 */
function findControlsInside(container: HTMLElement, params: SwiperParams) {
  const result = { ...params };

  MODULES_WITH_OWN_ELEMENT.forEach((moduleName) => {
    const selector = params[moduleName]?.el;
    if (typeof selector !== 'string') return;

    result[moduleName] = {
      ...params[moduleName],
      el: container.querySelector(selector),
    };
  });

  return result;
}

// Когда элемент убирают из DOM, swiper уничтожается, но пустой инстанс остаётся
// на элементе. Такой слайдер можно поднять заново.
const isInitialized = (container: SwiperContainer) =>
  !!container.swiper && !container.swiper.destroyed;

/**
 * Поднимает слайдер на `<swiper-container init="false">`.
 *
 * Шаблон: `<swiper-container ref="swiperEl" init="false">`,
 * скрипт: `const swiperEl = useSwiperElement(options)`.
 * Инстанс swiper после инициализации — `swiperEl.value.swiper`.
 *
 * Параметры отдаём из JS, а не атрибутами в шаблоне: Vue 2 превращает объект
 * в атрибуте в строку, а обработчики из `on` после инициализации уже не подключить.
 */
export default function useSwiperElement(params: SwiperParams) {
  const container = ref<SwiperContainer | null>(null);

  const init = () => {
    if (!container.value || isInitialized(container.value)) return;

    Object.assign(container.value, findControlsInside(container.value, params));
    container.value.initialize();
  };

  // Слайдер есть в DOM сразу после монтирования...
  onMounted(init);
  // ...или появляется позже, когда v-if дождётся данных
  watch(container, init, { flush: 'post' });

  return container;
}
